import type { GetServerSidePropsContext } from 'next';

import { LOCALE_COOKIE_KEY } from '../../middleware';

// Base namespaces used by all pages (from i18n.json "*" pattern)
const BASE_NAMESPACES = ['meta', 'errors', 'commons', 'layout_platform', 'sparks'];
const LOCALE_COOKIE_NAME = 'NEXT_LOCALE';
const SUPPORTED_LOCALES = ['en', 'fr', 'es'];
const DEFAULT_LOCALE = 'en';

class TranslationService {
    /**
     * Get locale from cookie in server-side context
     */
    getLocaleFromContext = (context: GetServerSidePropsContext): string => {
        const cookieHeader = context.req?.headers?.cookie || '';
        const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE_KEY}=([^;]+)`));
        const cookieLocale = match?.[1];
        if (cookieLocale && SUPPORTED_LOCALES.includes(cookieLocale)) {
            return cookieLocale;
        }
        return DEFAULT_LOCALE;
    };

    loadMessages = async (locale: string, additionalNamespaces: string[] = []) => {
        // Combine base namespaces with page-specific ones
        const allNamespaces = [...BASE_NAMESPACES, ...additionalNamespaces];
        const messages = {};

        for (const namespace of allNamespaces) {
            try {
                const module = await import(`../../../locales/${locale}/${namespace}.json`);
                messages[namespace] = module.default;
            } catch (error) {
                console.warn(`Failed to load ${namespace} for locale ${locale}:`, error);
            }
        }

        return messages;
    };

    // Convenience function for pages that only need base namespaces
    loadBaseMessages = async (locale: string) => this.loadMessages(locale, []);

    // Advanced helper that reads the i18n.json configuration to determine namespaces
    loadPageMessages = async (locale: string, pathname: string) => {
        try {
            // Import the i18n configuration
            const i18nConfig = await import('../../../i18n.json');
            const pages = i18nConfig.default.pages;

            // Get page-specific namespaces
            const pageNamespaces = pages[pathname] ?? [];
            return this.loadMessages(locale, pageNamespaces); // loadMessages already includes base
        } catch (error) {
            console.warn(
                `Failed to load page configuration for ${pathname}, falling back to base messages:`,
                error
            );
            return this.loadBaseMessages(locale);
        }
    };

    getTranslationMessages = (
        pageNamespaces: string[] | string = [],
        options: { pathname?: string } = {}
    ) => {
        return async (context: GetServerSidePropsContext) => {
            // Read locale from cookie
            const locale = this.getLocaleFromContext(context);

            if (options.pathname) {
                // Use automatic configuration from i18n.json
                return {
                    props: {
                        messages: await this.loadPageMessages(locale, options.pathname),
                        locale
                    }
                };
            } else if (typeof pageNamespaces === 'string') {
                // Single namespace provided
                return {
                    props: {
                        messages: await this.loadMessages(locale, [pageNamespaces]),
                        locale
                    }
                };
            } else if (pageNamespaces.length === 0) {
                // Only base namespaces
                return {
                    props: {
                        messages: await this.loadBaseMessages(locale),
                        locale
                    }
                };
            } else {
                // Array of namespaces provided
                return {
                    props: {
                        messages: await this.loadMessages(locale, pageNamespaces),
                        locale
                    }
                };
            }
        };
    };

    // New method for migration pattern using route constants
    getTranslationMessagesFromRoute = (route: string) => {
        return async (context: GetServerSidePropsContext) => {
            // Read locale from cookie
            const locale = this.getLocaleFromContext(context);

            try {
                // Load i18n.json configuration dynamically
                const i18nConfig = await import('../../../i18n.json');
                const pageNamespaces = i18nConfig.default.pages[route] ?? [];

                return {
                    props: {
                        messages: await this.loadMessages(locale, pageNamespaces),
                        locale
                    }
                };
            } catch (error) {
                console.warn(`Failed to load namespaces for route ${route}:`, error);
                return {
                    props: {
                        messages: await this.loadBaseMessages(locale),
                        locale
                    }
                };
            }
        };
    };

    getTranslationKey = (namespace: string | undefined, key: string) => {
        const namespacePattern = /^[^:\s]+:[^:\s]+$/;
        let translationKey = key;

        if (key.includes(' ')) return key;

        if (namespacePattern.test(key)) {
            if (key.includes(' ')) return key.split(':', 2)[1];

            translationKey = key.split(':', 2).join('.');
        }
        if (key.includes(' ')) {
            return key;
        }
        if (namespace && !namespacePattern.test(key)) {
            translationKey = `${namespace}.${translationKey}`;
        }

        return translationKey;
    };
}

const translationService = new TranslationService();

export { translationService as TranslationService };

// Usage examples in getServerSideProps:
// Locale is read from NEXT_LOCALE cookie on each request.
//
// For pages with only base namespaces:
// export const getServerSideProps = TranslationService.getTranslationMessages();
//
// For pages with additional namespaces:
// export const getServerSideProps = TranslationService.getTranslationMessages(['signin', 'home']);
// export const getServerSideProps = TranslationService.getTranslationMessages('signin'); // single namespace
//
// For automatic configuration from i18n.json:
// export const getServerSideProps = TranslationService.getTranslationMessages([], { pathname: '/forgot-password' });
//
// Manual usage:
// const locale = TranslationService.getLocaleFromContext(context);
// messages: await TranslationService.loadBaseMessages(locale)
// messages: await TranslationService.loadMessages(locale, ['signin', 'home'])
// messages: await TranslationService.loadPageMessages(locale, '/forgot-password')
